import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const STATUSES = ["Created", "Pending", "Paid", "Failed", "Cancelled", "Refunded"];

const STATUS_STYLES = {
  Created: "bg-neutral-200 text-neutral-600",
  Pending: "bg-amber-100 text-amber-700",
  Paid: "bg-emerald-100 text-emerald-700",
  Failed: "bg-red-100 text-red-600",
  Cancelled: "bg-neutral-200 text-neutral-500",
  Refunded: "bg-blue-100 text-blue-700",
};

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" })
    : "";

export default function AdminOrders() {
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState("");

  const load = useCallback(async (s) => {
    try {
      const { data } = await api.get("/admin/orders", { params: s ? { status: s } : {} });
      setItems(data);
    } catch {
      setItems([]);
    }
  }, []);

  useEffect(() => {
    load(status);
  }, [status, load]);

  const changeStatus = async (order, next) => {
    setItems((list) => list.map((o) => (o.id === order.id ? { ...o, status: next } : o)));
    try {
      await api.patch(`/admin/orders/${order.id}`, { status: next });
    } catch {
      load(status);
    }
  };

  return (
    <section data-testid="admin-orders">
      <div className="mb-6 flex flex-wrap gap-2">
        {["", ...STATUSES].map((s) => (
          <button
            key={s || "all"}
            data-testid={`order-filter-${s ? s.toLowerCase() : "all"}`}
            onClick={() => setStatus(s)}
            className={`rounded-full border px-4 py-2 text-xs font-medium transition-colors ${
              status === s ? "border-ink bg-ink text-cream" : "border-line bg-white text-[#5C5C5C] hover:border-rose hover:text-rose"
            }`}
          >
            {s || "All"}
          </button>
        ))}
      </div>

      {!items ? (
        <div className="flex justify-center py-20"><Loader2 size={26} className="animate-spin text-rose" /></div>
      ) : items.length === 0 ? (
        <p data-testid="orders-empty" className="rounded-2xl border border-line bg-white py-16 text-center text-sm text-[#5C5C5C]">
          No orders yet. Orders appear here when customers pay via Razorpay.
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {items.map((o) => (
            <article key={o.id} data-testid={`order-row-${o.id}`} className="rounded-2xl border border-line bg-white p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="font-serif text-xl font-medium">{o.customer_name}</h3>
                  <p className="mt-0.5 text-xs text-[#5C5C5C]">{formatDate(o.created_at)}</p>
                </div>
                <select
                  data-testid={`order-status-${o.id}`}
                  value={o.status}
                  onChange={(e) => changeStatus(o, e.target.value)}
                  aria-label="Order status"
                  className={`cursor-pointer rounded-full border-0 px-3.5 py-2 text-xs font-semibold outline-none ${STATUS_STYLES[o.status] || ""}`}
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="mt-4 grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
                <p className="text-[#5C5C5C]">Product: <span className="text-ink">{o.product_name}</span></p>
                <p className="text-[#5C5C5C]">Amount: <span className="font-semibold text-ink">₹{(o.amount / 100).toLocaleString("en-IN")}</span> <span className="text-xs">({o.currency})</span></p>
                <p className="text-[#5C5C5C]">Phone: <a href={`tel:+${o.customer_phone}`} className="text-ink hover:text-rose">{o.customer_phone}</a></p>
                {o.customer_email && <p className="text-[#5C5C5C]">Email: <span className="text-ink">{o.customer_email}</span></p>}
                <p className="text-[#5C5C5C]">Order ID: <span className="font-mono text-xs text-ink">{o.razorpay_order_id || "—"}</span></p>
                <p className="text-[#5C5C5C]">Payment ID: <span className="font-mono text-xs text-ink">{o.razorpay_payment_id || "—"}</span></p>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
